/**
 * The daily AI rate limit, as the admin UI needs to understand it.
 *
 * ---- This is a MIRROR ------------------------------------------------------
 *
 * Every type, default and resolve rule below is owned by watchlock's
 * `functions/src/aiRateLimitConfig.ts` and copied here by hand. The admin app
 * cannot import it — different repo, different tsconfig, and that module is
 * deliberately a leaf with no package boundary of its own.
 *
 * A callable serving these defaults would be the stronger contract, and it is
 * the pattern the AI scoring penalties panel already uses (`defaultsSource` →
 * `getAiScoringPenaltyDefaults`). There is no such callable for the rate limit,
 * and adding one is a functions change. Until there is, `aiRateLimitConfig.test.ts`
 * pins every mirrored value, so a drift is a red test rather than a wrong number
 * on an operator's screen.
 *
 * Keep in step with watchlock. The precedent for hand-mirroring rather than
 * importing is that module's own `AiTierId`, which redeclares the tier list from
 * `aiCredits.ts` for exactly the same reason.
 *
 * ---- What this is used for -------------------------------------------------
 *
 * Two surfaces, both read-only against this file: the per-user cap override on
 * the Edit User modal (which has to show the effective cap a user is actually
 * on, and cannot compute it without the tier table), and the shared validation
 * behind every cap input.
 *
 * Writing the config node is NOT done here — that goes through the schema-driven
 * `SchemaConfigPanel`, same as every other `configuration/*` node.
 *
 * ---- No imports, deliberately ----------------------------------------------
 *
 * Same split, and the same reason, as the watchlock module this mirrors: the
 * decisions live in a leaf and the I/O lives above it, in `aiRateLimit.ts`.
 * Reaching `./firebase` from here would pull the whole Firebase service module
 * — and `heic2any`, which touches `window` — into every test that imports a
 * type from this file, and the suite runs in node. The split is what makes the
 * resolve rules testable at all.
 */

/** RTDB node holding the limiter config. Mirrors `AI_RATE_LIMIT_CONFIG_PATH`. */
export const AI_RATE_LIMIT_CONFIG_PATH = 'configuration/aiRateLimit';

/** Which limiter is live. */
export type AiRateLimitMode = 'credits' | 'daily';

/** Subscription tiers, as they appear in `users/{uid}.plan`. */
export type AiTierId = 'free' | 'collector' | 'curator' | 'horologist';

export const AI_TIER_IDS: AiTierId[] = [
  'free',
  'collector',
  'curator',
  'horologist',
];

/**
 * The AI surfaces a call is attributed to in the `ai_usage_events` audit trail.
 *
 * Finer-grained than the four callables that write it — `referenceLookup` runs
 * both a catalogue lookup and, on some paths, a web search, and the audit trail
 * exists to tell those apart. Read by the OpenAI usage panel to label and order
 * the call-volume breakdown; a value outside this list lands in that panel's
 * "unrecognised" counter rather than in a bar.
 */
export type AiCallType =
  | 'identify'
  | 'smartSearch'
  | 'referenceLookup'
  | 'webSearch'
  | 'imageWebSearch'
  | 'enrichment';

/** Render order for the call-volume breakdown, and the mirror the test pins. */
export const AI_CALL_TYPES: AiCallType[] = [
  'identify',
  'smartSearch',
  'referenceLookup',
  'webSearch',
  'imageWebSearch',
  'enrichment',
];

/** A tier's daily allowance. `'unlimited'` is a real, writable operator value. */
export type AiCap = number | 'unlimited';

export type AiCapTable = Record<AiTierId, AiCap>;

export interface AiFreeGates {
  webSearch: boolean;
  reverseImageSearch: boolean;
}

export interface AiRateLimitConfig {
  mode: AiRateLimitMode;
  caps: AiCapTable;
  freeGates: AiFreeGates;
}

export const DEFAULT_AI_RATE_LIMIT_MODE: AiRateLimitMode = 'credits';

/**
 * Shipped caps. Free is 0 deliberately — under 'daily' a free user gets nothing
 * until an operator writes them a number.
 */
export const DEFAULT_AI_CAPS: AiCapTable = {
  free: 0,
  collector: 20,
  curator: 50,
  horologist: 'unlimited',
};

export const DEFAULT_AI_FREE_GATES: AiFreeGates = {
  webSearch: false,
  reverseImageSearch: false,
};

export const DEFAULT_AI_RATE_LIMIT_CONFIG: AiRateLimitConfig = {
  mode: DEFAULT_AI_RATE_LIMIT_MODE,
  caps: DEFAULT_AI_CAPS,
  freeGates: DEFAULT_AI_FREE_GATES,
};

const VALID_MODES: AiRateLimitMode[] = ['credits', 'daily'];

function asNode(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
}

/** Mirror of `resolveAiRateLimitMode`. Unrecognised resolves to 'credits'. */
export function resolveAiRateLimitMode(raw: unknown): AiRateLimitMode {
  const node = asNode(raw);
  if (!node) return DEFAULT_AI_RATE_LIMIT_MODE;
  const rawMode = node.mode;
  if (typeof rawMode === 'string') {
    const needle = rawMode.trim().toLowerCase();
    const found = VALID_MODES.find((m) => m === needle);
    if (found) return found;
  }
  return DEFAULT_AI_RATE_LIMIT_MODE;
}

/**
 * Mirror of `resolveCap`. A cap of 0 IS meaningful, so nothing here may lean on
 * falsiness, and a fractional value floors rather than failing.
 */
function resolveCap(raw: unknown, dflt: AiCap): AiCap {
  if (typeof raw === 'string' && raw.trim().toLowerCase() === 'unlimited') {
    return 'unlimited';
  }
  if (raw === null || raw === undefined || typeof raw === 'boolean') return dflt;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0) return dflt;
  return Math.floor(n);
}

/**
 * Mirror of `resolveAiCaps`. A partial `caps` node is normal — every tier the
 * node omits keeps its shipped default.
 */
export function resolveAiCaps(raw: unknown): AiCapTable {
  const node = asNode(raw);
  const capsNode = node ? asNode(node.caps) : null;
  if (!capsNode) return { ...DEFAULT_AI_CAPS };
  const out = { ...DEFAULT_AI_CAPS };
  for (const tier of AI_TIER_IDS) {
    if (capsNode[tier] !== undefined) {
      out[tier] = resolveCap(capsNode[tier], DEFAULT_AI_CAPS[tier]);
    }
  }
  return out;
}

/** Mirror of `resolveAiFreeGates`. Only the literal boolean `true` gates. */
export function resolveAiFreeGates(raw: unknown): AiFreeGates {
  const node = asNode(raw);
  const gatesNode = node ? asNode(node.freeGates) : null;
  if (!gatesNode) return { ...DEFAULT_AI_FREE_GATES };
  return {
    webSearch: gatesNode.webSearch === true,
    reverseImageSearch: gatesNode.reverseImageSearch === true,
  };
}

export function resolveAiRateLimitConfig(raw: unknown): AiRateLimitConfig {
  return {
    mode: resolveAiRateLimitMode(raw),
    caps: resolveAiCaps(raw),
    freeGates: resolveAiFreeGates(raw),
  };
}

/**
 * Mirror of `effectiveCapFor`. A per-user override wins over the tier default in
 * BOTH directions; a malformed one falls through to the tier rather than to
 * zero, so a bad override can never lock a paying user out.
 */
export function effectiveCapFor(params: {
  plan: unknown;
  capOverride?: unknown;
  caps: AiCapTable;
}): AiCap {
  const { plan, capOverride, caps } = params;
  if (capOverride !== undefined && capOverride !== null) {
    if (
      typeof capOverride === 'string' &&
      capOverride.trim().toLowerCase() === 'unlimited'
    ) {
      return 'unlimited';
    }
    if (
      typeof capOverride === 'number' &&
      Number.isFinite(capOverride) &&
      capOverride >= 0
    ) {
      return Math.floor(capOverride);
    }
  }
  const tier = AI_TIER_IDS.find((t) => t === plan);
  return caps[tier ?? 'free'];
}

// ── Shared cap input value + validation ──────────────────────────────────────

/** The in-progress value of a cap input. `undefined` is an empty box. */
export type CapValue = AiCap | undefined;

export type CapCheck = { ok: true } | { ok: false; reason: string };

/**
 * Validate one cap, for both the per-tier inputs and the per-user override.
 *
 * The rules come from `resolveCap`, which does not reject — it falls back to a
 * default. So every value this rejects would otherwise have been accepted by the
 * form and then silently ignored by the backend, which is the failure worth
 * catching here:
 *
 *  - a non-integer FLOORS (20.7 becomes 20), so it is rejected rather than
 *    quietly truncated;
 *  - a negative falls back to the tier default, which looks like the save did
 *    nothing;
 *  - 0 is valid and must survive — it is the free tier's shipped default.
 */
export function checkCapValue(value: CapValue): CapCheck {
  if (value === 'unlimited') return { ok: true };
  if (value === undefined) return { ok: false, reason: 'Required' };
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return { ok: false, reason: 'Must be a number' };
  }
  if (value < 0) return { ok: false, reason: 'Must be 0 or more' };
  if (!Number.isInteger(value)) {
    return { ok: false, reason: 'Must be a whole number' };
  }
  return { ok: true };
}

/** Human-readable cap, for the effective-cap readouts. */
export function formatCap(cap: AiCap): string {
  return cap === 'unlimited' ? 'unlimited' : `${cap} / day`;
}
